import PropTypes from "prop-types";

/**
 * Normalize the coordinates to the format used by the map components.
 *
 * @param {PropTypes.InferType<typeof CoordinatesShape>} [coordinates]
 * @returns {{lat:number, lng:number} | undefined}
 */
export const convertCoordinates = (coordinates) => {
  if (!coordinates) {
    return undefined;
  }
  if ("latitude" in coordinates && "longitude" in coordinates) {
    return {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    };
  }
  if ("lat" in coordinates && "lng" in coordinates) {
    return {
      lat: coordinates.lat,
      lng: coordinates.lng,
    };
  }
  throw new Error(
    `Invalid coordinates "${JSON.stringify(
      coordinates
    )}". Should have lat/lng or latitude/longitude.`
  );
};

/**
 * Normalize the coordinates inside a {@link LocationInputShape location} object.
 *
 * @param {TLocationInputShape} [location]
 */
const convertLocation = (location) => {
  if (!location) {
    return undefined;
  }
  return {
    ...location,
    coordinates: convertCoordinates(location.coordinates),
  };
};

/** @typedef {PropTypes.InferType<typeof SchemeCoordinates>} TSchemeCoordinates */
const SchemeCoordinates = PropTypes.shape({
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
});

/** @typedef {PropTypes.InferType<typeof ComponentCoordinates>} TComponentCoordinates */
const ComponentCoordinates = PropTypes.shape({
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
});

const CoordinatesShape = PropTypes.oneOfType([
  SchemeCoordinates,
  ComponentCoordinates,
]);

/** @typedef {PropTypes.InferType<typeof LocationInputShape>} TLocationInputShape */
const LocationInputShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  address: PropTypes.shape({
    streetAddress: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    postalCode: PropTypes.string.isRequired,
    locality: PropTypes.string.isRequired,
  }).isRequired,
  coordinates: CoordinatesShape.isRequired,
});

/** @deprecated - Use {@link LocationInputShape} instead */
const LocationInternalShape = LocationInputShape;

export {
  convertLocation,
  LocationInputShape,
  LocationInternalShape,
  CoordinatesShape,
};
